import type { ApiResponse } from "@/features/auth/services/authApi";
import { apiClient } from "@/shared/services/api";

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  lineTotal: number;
  unitPrice?: number;
  unitCost?: number;
  vatPercentage?: number;
  vatAmount?: number;
  discountAmount?: number;
  variety?: string;
  varietyName?: string;
  orderItemId?: string;
  inputBatchId?: string;
  inputId?: string;
  lineType?: number;
  lineTypeName?: string;
  postingAccountId?: string;
  postingAccountCode?: string;
  postingAccountName?: string;
  chargeType?: number;
  batchNumber?: string;
}

export interface SupplierInvoiceSummary {
  id: string;
  invoiceNumber: string;
  supplierInvoiceNumber: string;
  supplierName: string;
  purchaseOrderNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: number;
  statusName: string;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
  isOverdue: boolean;
  daysOverdue: number;
  itemCount: number;
  items?: InvoiceLineItem[];
  createdAt: string;
}

export interface SalesInvoiceSummary {
  id: string;
  invoiceNumber: string;
  customerName: string;
  orderNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: number;
  statusName: string;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
  isOverdue: boolean;
  daysOverdue: number;
  createdAt: string;
  // Void request staging
  voidRequestedByUserName?: string;
  voidRequestedAt?: string;
  voidRequestReason?: string;
  refundRequestedWithVoid?: boolean;
  requestedRefundAmount?: number;
  requestedRefundPaymentMethod?: string;
}

export interface ReceiptHistoryItem {
  id: string;
  receiptNumber: string;
  printedAt: string;
  printedByUserName: string;
  printCount: number;
  isReprint: boolean;
  reprintReason?: string;
  ipAddress?: string;
}

export interface SupplierInvoiceDetail extends SupplierInvoiceSummary {
  supplierId: string;
  supplierPhone?: string;
  subtotal: number;
  vatAmount: number;
  approvedByUserName?: string;
  approvedAt?: string;
  paidByUserName?: string;
  paidAt?: string;
  paymentReference?: string;
  paymentMethod?: string;
  disputeReason?: string;
  disputedAt?: string;
  voidReason?: string;
  voidedAt?: string;
  notes?: string;
  updatedAt: string;
  items: InvoiceLineItem[];
}

export interface SalesInvoiceDetail {
  id: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  customerPhone?: string;
  orderId?: string;
  orderNumber?: string;
  invoiceDate: string;
  dueDate: string;
  status: number;
  statusName: string;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  discountAmount: number;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
  issuedAt?: string;
  issuedByUserName?: string;
  voidedAt?: string;
  voidedByUserName?: string;
  voidReason?: string;
  // Void request staging fields
  voidRequestedByUserName?: string;
  voidRequestedAt?: string;
  voidRequestReason?: string;
  notes?: string;
  terms?: string;
  isOverdue: boolean;
  daysOverdue: number;
  items: InvoiceLineItem[];
  printHistory: ReceiptHistoryItem[];
  createdAt: string;
  updatedAt: string;
  // Customer discount snapshot from the linked order (line totals already reflect it).
  orderOriginalAmount?: number;
  orderDiscountAmount?: number;
  orderDiscountPercentage?: number;
  /** "Campaign" | "Customer" | "Group" | "None". */
  orderDiscountSource?: string;
}

export interface CreateSalesInvoiceItem {
  description: string;
  quantity: number;
  unit: number;
  unitPrice: number;
  discountAmount: number;
  variety?: string;
  orderItemId?: string;
  chargeType: number;
}

export interface CreateSalesInvoiceRequest {
  customerId: string;
  invoiceDate: string;
  dueDate: string;
  taxRate: number;
  discountAmount: number;
  notes?: string;
  terms?: string;
  items: CreateSalesInvoiceItem[];
}

export interface GenerateSalesInvoiceFromOrderRequest {
  orderId: string;
  dueDate: string;
  taxRate: number;
  discountAmount: number;
  notes?: string;
  terms?: string;
}

export interface SupplierInvoicePaymentRequest {
  invoiceId: string;
  amountPaid: number;
  paymentDate: string;
  paymentMethodAccountId: string;
  paymentMethod?: string;
  paymentReference?: string;
  notes?: string;
}

export interface CreateSupplierInvoiceLineRequest {
  lineType: number;
  inputId?: string | null;
  postingAccountId: string;
  description: string;
  quantity: number;
  unit: string;
  unitCost: number;
  vatPercentage?: number | null;
}

export interface CreateSupplierInvoiceRequest {
  supplierInvoiceNumber?: string;
  supplierId: string;
  purchaseOrderNumber?: string;
  invoiceDate: string;
  dueDate: string;
  notes?: string;
  items: CreateSupplierInvoiceLineRequest[];
}

export interface SupplierInvoiceReasonRequest {
  invoiceId: string;
  disputeReason?: string;
  voidReason?: string;
}

export interface VoidSalesInvoiceRequest {
  invoiceId: string;
  voidReason: string;
}

export interface RequestVoidRequest {
  invoiceId: string;
  reason: string;
  initiateRefund?: boolean;
  refundAmount?: number;
  refundPaymentMethod?: string;
  refundPaymentReference?: string;
  refundNotes?: string;
}

export interface ApproveVoidRequest {
  invoiceId: string;
  notes?: string;
}

export interface RejectVoidRequest {
  invoiceId: string;
  reason: string;
}

export interface CreditNoteLineItem {
  id: string;
  invoiceItemId?: string;
  description: string;
  variety?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
  chargeType?: number;
  postingAccountId?: string;
}

export interface CreditNoteSummary {
  id: string;
  creditNoteNumber: string;
  invoiceId: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  issuedDate: string;
  totalAmount: number;
  reason: string;
  issuedByUserName: string;
  createdAt: string;
}

export interface CreditNoteDetail extends CreditNoteSummary {
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  discountAmount: number;
  notes?: string;
  items: CreditNoteLineItem[];
}

export interface PrintReceiptRequest {
  invoiceId: string;
  paymentId: string;
  isReprint: boolean;
  reprintReason?: string;
  ipAddress?: string;
}

export interface PrintedReceipt {
  receiptPrintId: string;
  receiptNumber: string;
  isReprint: boolean;
  printCount: number;
  printedAt: string;
  invoiceNumber: string;
  customerName: string;
  customerPhone?: string;
  customerAddress?: string;
  invoiceDate: string;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  discountAmount: number;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
  orderNumber?: string;
  notes?: string;
  items: InvoiceLineItem[];
}

const unwrap = <T>(response: ApiResponse<T>): T => response.data;

const getFileNameFromDisposition = (disposition?: string): string | undefined => {
  if (!disposition) return undefined;

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].replace(/"/g, ""));

  const fallbackMatch = disposition.match(/filename="?([^";]+)"?/i);
  return fallbackMatch?.[1];
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const openBlob = (blob: Blob, target?: Window | null) => {
  const url = URL.createObjectURL(blob);
  if (target) {
    target.location.href = url;
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export const invoicingApi = {
  createSupplierInvoice: async (payload: CreateSupplierInvoiceRequest): Promise<SupplierInvoiceDetail> => {
    const { data } = await apiClient.post<ApiResponse<SupplierInvoiceDetail>>("/supplier-invoices", payload);
    return unwrap(data);
  },
  getAllSupplierInvoices: async (pageNumber = 1, pageSize = 20, status?: number, overdue?: boolean, search?: string): Promise<PagedResult<SupplierInvoiceSummary>> => {
    const { data } = await apiClient.get<ApiResponse<PagedResult<SupplierInvoiceSummary>>>("/supplier-invoices", {
      params: { pageNumber, pageSize, status, overdue, search },
    });
    return unwrap(data);
  },

  getSupplierInvoiceById: async (id: string): Promise<SupplierInvoiceDetail> => {
    const { data } = await apiClient.get<ApiResponse<SupplierInvoiceDetail>>(
      `/supplier-invoices/${id}`
    );
    return unwrap(data);
  },

  getSupplierInvoicePdf: async (id: string): Promise<{ blob: Blob; filename: string }> => {
    const response = await apiClient.get<Blob>(`/supplier-invoices/${id}/pdf`, {
      responseType: "blob",
    });
    return {
      blob: response.data,
      filename: getFileNameFromDisposition(response.headers["content-disposition"]) ?? "Supplier-Invoice.pdf",
    };
  },

  viewSupplierInvoicePdf: async (id: string): Promise<void> => {
    const target = window.open("about:blank", "_blank");
    try {
      const { blob } = await invoicingApi.getSupplierInvoicePdf(id);
      openBlob(blob, target);
    } catch (error) {
      target?.close();
      throw error;
    }
  },

  downloadSupplierInvoicePdf: async (id: string): Promise<void> => {
    const { blob, filename } = await invoicingApi.getSupplierInvoicePdf(id);
    downloadBlob(blob, filename);
  },

  getSupplierInvoiceByNumber: async (
    invoiceNumber: string
  ): Promise<SupplierInvoiceDetail> => {
    const { data } = await apiClient.get<ApiResponse<SupplierInvoiceDetail>>(
      `/supplier-invoices/by-number/${invoiceNumber}`
    );
    return unwrap(data);
  },

  getSupplierInvoicesBySupplierId: async (
    supplierId: string
  ): Promise<SupplierInvoiceSummary[]> => {
    const { data } = await apiClient.get<ApiResponse<SupplierInvoiceSummary[]>>(
      `/supplier-invoices/supplier/${supplierId}`
    );
    return unwrap(data);
  },

  getSupplierInvoicesByStatus: async (
    status: number
  ): Promise<SupplierInvoiceSummary[]> => {
    const { data } = await apiClient.get<ApiResponse<SupplierInvoiceSummary[]>>(
      `/supplier-invoices/by-status/${status}`
    );
    return unwrap(data);
  },

  getOverdueSupplierInvoices: async (): Promise<SupplierInvoiceSummary[]> => {
    const { data } = await apiClient.get<ApiResponse<SupplierInvoiceSummary[]>>(
      "/supplier-invoices/overdue"
    );
    return unwrap(data);
  },

  generateSupplierInvoiceFromBatch: async (
    batchId: string
  ): Promise<SupplierInvoiceDetail> => {
    const { data } = await apiClient.post<ApiResponse<SupplierInvoiceDetail>>(
      `/supplier-invoices/generate-from-batch/${batchId}`
    );
    return unwrap(data);
  },

  generateSupplierInvoicesFromBatches: async (
    batchIds: string[]
  ): Promise<SupplierInvoiceDetail> => {
    const { data } = await apiClient.post<ApiResponse<SupplierInvoiceDetail>>(
      "/supplier-invoices/generate-from-batches",
      batchIds
    );
    return unwrap(data);
  },

  approveSupplierInvoice: async (invoiceId: string): Promise<SupplierInvoiceDetail> => {
    const { data } = await apiClient.post<ApiResponse<SupplierInvoiceDetail>>(
      `/supplier-invoices/${invoiceId}/approve`
    );
    return unwrap(data);
  },

  recordSupplierInvoicePayment: async (
    invoiceId: string,
    payload: SupplierInvoicePaymentRequest
  ): Promise<SupplierInvoiceDetail> => {
    const { data } = await apiClient.post<ApiResponse<SupplierInvoiceDetail>>(
      `/supplier-invoices/${invoiceId}/payment`,
      payload
    );
    return unwrap(data);
  },

  disputeSupplierInvoice: async (
    invoiceId: string,
    payload: SupplierInvoiceReasonRequest
  ): Promise<SupplierInvoiceDetail> => {
    const { data } = await apiClient.post<ApiResponse<SupplierInvoiceDetail>>(
      `/supplier-invoices/${invoiceId}/dispute`,
      payload
    );
    return unwrap(data);
  },

  voidSupplierInvoice: async (
    invoiceId: string,
    payload: SupplierInvoiceReasonRequest
  ): Promise<SupplierInvoiceDetail> => {
    const { data } = await apiClient.post<ApiResponse<SupplierInvoiceDetail>>(
      `/supplier-invoices/${invoiceId}/void`,
      payload
    );
    return unwrap(data);
  },

  getAllSalesInvoices: async (pageNumber = 1, pageSize = 20, status?: number, overdue?: boolean, search?: string): Promise<PagedResult<SalesInvoiceSummary>> => {
    const { data } = await apiClient.get<ApiResponse<PagedResult<SalesInvoiceSummary>>>("/invoices", {
      params: { pageNumber, pageSize, status, overdue, search },
    });
    return unwrap(data);
  },

  getSalesInvoiceById: async (id: string): Promise<SalesInvoiceDetail> => {
    const { data } = await apiClient.get<ApiResponse<SalesInvoiceDetail>>(
      `/invoices/${id}`
    );
    return unwrap(data);
  },

  getSalesInvoicePdf: async (id: string): Promise<{ blob: Blob; filename: string }> => {
    const response = await apiClient.get<Blob>(`/invoices/${id}/pdf`, {
      responseType: "blob",
    });
    return {
      blob: response.data,
      filename: getFileNameFromDisposition(response.headers["content-disposition"]) ?? "Invoice.pdf",
    };
  },

  viewSalesInvoicePdf: async (id: string): Promise<void> => {
    const target = window.open("about:blank", "_blank");
    try {
      const { blob } = await invoicingApi.getSalesInvoicePdf(id);
      openBlob(blob, target);
    } catch (error) {
      target?.close();
      throw error;
    }
  },

  downloadSalesInvoicePdf: async (id: string): Promise<void> => {
    const { blob, filename } = await invoicingApi.getSalesInvoicePdf(id);
    downloadBlob(blob, filename);
  },

  getSalesInvoiceByNumber: async (
    invoiceNumber: string
  ): Promise<SalesInvoiceDetail> => {
    const { data } = await apiClient.get<ApiResponse<SalesInvoiceDetail>>(
      `/invoices/by-number/${invoiceNumber}`
    );
    return unwrap(data);
  },

  getSalesInvoicesByCustomerId: async (
    customerId: string
  ): Promise<SalesInvoiceSummary[]> => {
    const { data } = await apiClient.get<ApiResponse<SalesInvoiceSummary[]>>(
      `/invoices/customer/${customerId}`
    );
    return unwrap(data);
  },

  getSalesInvoicesByOrderId: async (orderId: string): Promise<SalesInvoiceSummary[]> => {
    const { data } = await apiClient.get<ApiResponse<SalesInvoiceSummary[]>>(
      `/invoices/order/${orderId}`
    );
    return unwrap(data);
  },

  getSalesInvoicesByStatus: async (status: number): Promise<SalesInvoiceSummary[]> => {
    const { data } = await apiClient.get<ApiResponse<SalesInvoiceSummary[]>>(
      `/invoices/by-status/${status}`
    );
    return unwrap(data);
  },

  getOverdueSalesInvoices: async (): Promise<SalesInvoiceSummary[]> => {
    const { data } = await apiClient.get<ApiResponse<SalesInvoiceSummary[]>>(
      "/invoices/overdue"
    );
    return unwrap(data);
  },

  createSalesInvoice: async (
    payload: CreateSalesInvoiceRequest
  ): Promise<SalesInvoiceDetail> => {
    const { data } = await apiClient.post<ApiResponse<SalesInvoiceDetail>>(
      "/invoices",
      payload
    );
    return unwrap(data);
  },

  generateSalesInvoiceFromOrder: async (
    payload: GenerateSalesInvoiceFromOrderRequest
  ): Promise<SalesInvoiceDetail> => {
    const { data } = await apiClient.post<ApiResponse<SalesInvoiceDetail>>(
      "/invoices/generate-from-order",
      payload
    );
    return unwrap(data);
  },

  issueSalesInvoice: async (invoiceId: string): Promise<SalesInvoiceDetail> => {
    const { data } = await apiClient.post<ApiResponse<SalesInvoiceDetail>>(
      `/invoices/${invoiceId}/issue`
    );
    return unwrap(data);
  },

  requestVoidSalesInvoice: async (
    invoiceId: string,
    payload: RequestVoidRequest
  ): Promise<SalesInvoiceDetail> => {
    const { data } = await apiClient.post<ApiResponse<SalesInvoiceDetail>>(
      `/invoices/${invoiceId}/request-void`,
      payload
    );
    return unwrap(data);
  },

  getPendingVoidSalesInvoices: async (): Promise<SalesInvoiceSummary[]> => {
    const { data } = await apiClient.get<ApiResponse<SalesInvoiceSummary[]>>(
      "/invoices/pending-void"
    );
    return unwrap(data);
  },

  approveVoidSalesInvoice: async (
    invoiceId: string,
    payload: ApproveVoidRequest
  ): Promise<SalesInvoiceDetail> => {
    const { data } = await apiClient.post<ApiResponse<SalesInvoiceDetail>>(
      `/invoices/${invoiceId}/approve-void`,
      payload
    );
    return unwrap(data);
  },

  rejectVoidSalesInvoice: async (
    invoiceId: string,
    payload: RejectVoidRequest
  ): Promise<SalesInvoiceDetail> => {
    const { data } = await apiClient.post<ApiResponse<SalesInvoiceDetail>>(
      `/invoices/${invoiceId}/reject-void`,
      payload
    );
    return unwrap(data);
  },

  getAllCreditNotes: async (): Promise<CreditNoteSummary[]> => {
    const { data } = await apiClient.get<ApiResponse<CreditNoteSummary[]>>("/credit-notes");
    return unwrap(data);
  },

  getCreditNoteById: async (id: string): Promise<CreditNoteDetail> => {
    const { data } = await apiClient.get<ApiResponse<CreditNoteDetail>>(`/credit-notes/${id}`);
    return unwrap(data);
  },

  getCreditNoteByInvoiceId: async (invoiceId: string): Promise<CreditNoteDetail> => {
    const { data } = await apiClient.get<ApiResponse<CreditNoteDetail>>(`/credit-notes/by-invoice/${invoiceId}`);
    return unwrap(data);
  },

  printReceipt: async (
    invoiceId: string,
    payload: PrintReceiptRequest
  ): Promise<PrintedReceipt> => {
    const { data } = await apiClient.post<ApiResponse<PrintedReceipt>>(
      `/invoices/${invoiceId}/receipt/print`,
      payload
    );
    return unwrap(data);
  },

  getReceiptHistory: async (invoiceId: string): Promise<ReceiptHistoryItem[]> => {
    const { data } = await apiClient.get<ApiResponse<ReceiptHistoryItem[]>>(
      `/invoices/${invoiceId}/receipt/history`
    );
    return unwrap(data);
  },
};
